/**
 * Upload dialog for a **file** Source (ADR-0027 B4) — a file IS a source.
 *
 * Pick a CSV/Excel → it's parsed into Tray's own Postgres and registered as a
 * `file` source + its 1:1 queryable dataset. Name defaults to the filename stem.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, UploadCloud } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OVERRIDE_TYPES, type Source, sourcesApi } from "@/lib/api-sources";

export interface SourceUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog re-imports into THIS file source (replace its rows)
   *  instead of creating a new one — name/tags are hidden (ADR-0027 B4). */
  reuploadSource?: Source | null;
}

export function SourceUploadDialog({ open, onOpenChange, reuploadSource }: SourceUploadDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isReupload = !!reuploadSource;

  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  // Operator overrides of the inferred column type ({column: type}); a column
  // absent here keeps its inferred type. Reset whenever a new file/sheet is picked.
  const [overrides, setOverrides] = React.useState<Record<string, string>>({});
  // Chosen .xlsx worksheet ("" = first / not yet picked). A multi-sheet workbook
  // surfaces a picker; switching it re-parses that sheet.
  const [sheet, setSheet] = React.useState("");

  // Dry-run parse of the picked file → columns + sample, before committing.
  const preview = useMutation({
    mutationFn: (vars: { file: File; sheet?: string }) =>
      sourcesApi.previewFile(vars.file, vars.sheet),
  });

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setName("");
      setTags([]);
      setOverrides({});
      setSheet("");
      preview.reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPick = (f: File | null) => {
    setFile(f);
    if (f && !name) setName(f.name.replace(/\.[^.]+$/, ""));
    setOverrides({});
    setSheet("");
    preview.reset();
    if (f) preview.mutate({ file: f });
  };

  // Re-parse a different worksheet — overrides are column-specific, so drop them.
  const onPickSheet = (next: string | null) => {
    if (!file || !next || next === sheet) return;
    setSheet(next);
    setOverrides({});
    preview.mutate({ file, sheet: next });
  };

  const typeOptions = React.useMemo(
    () => OVERRIDE_TYPES.map((v) => ({ value: v, label: v })),
    [],
  );

  const submit = useMutation({
    mutationFn: () =>
      isReupload
        ? sourcesApi.reupload(reuploadSource!.id, file!)
        : sourcesApi.uploadFile({
            file: file!,
            name: name.trim() || undefined,
            tags,
            columnTypes: Object.keys(overrides).length ? overrides : undefined,
            sheet: sheet || undefined,
          }),
    onSuccess: () => {
      // A file source also (auto-)owns a dataset → refresh both catalogs.
      qc.invalidateQueries({ queryKey: ["sources"] });
      qc.invalidateQueries({ queryKey: ["datasets"] });
      onOpenChange(false);
    },
  });

  const canSubmit = !!file && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isReupload
              ? t("sources.reupload.title", "Re-importa il file")
              : t("sources.upload.title", "Carica un file")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-xs text-muted-foreground">
            {isReupload
              ? t("sources.reupload.hint", "Sostituisce le righe di «{{name}}» con quelle del nuovo file. Lo schema può cambiare (colonne aggiunte/rimosse).", { name: reuploadSource?.name ?? "" })
              : t(
                  "sources.upload.hint",
                  "CSV o Excel (.xlsx). Diventa una sorgente di tipo file: le righe vengono importate nel database di Tray. Il dataset interrogabile viene creato automaticamente — non devi aggiungerlo a mano.",
                )}
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="src-file">{t("sources.upload.file", "File")}</Label>
            <Input
              id="src-file"
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xlsm"
              onChange={(e) => onPick(e.target.files?.[0] ?? null)}
            />
          </div>

          {preview.isPending && (
            <p className="text-xs text-muted-foreground">{t("sources.upload.parsing", "Analisi del file…")}</p>
          )}
          {preview.isError && (
            <Alert tone="danger" title={t("sources.upload.parseFailed", "File non leggibile")}>
              {(preview.error as Error).message}
            </Alert>
          )}
          {preview.data && preview.data.sheets.length > 1 && (
            <div className="space-y-1.5">
              <Label htmlFor="src-up-sheet">{t("sources.upload.sheet", "Foglio")}</Label>
              <Combobox
                id="src-up-sheet"
                options={preview.data.sheets.map((s) => ({ value: s, label: s }))}
                value={sheet || preview.data.sheet}
                onChange={onPickSheet}
                disabled={preview.isPending}
                className="w-56"
              />
            </div>
          )}

          {preview.data && (
            <div className="space-y-1.5">
              <Label>
                {t("sources.upload.previewLabel", "Anteprima")} ·{" "}
                <span className="text-muted-foreground">
                  {t("sources.upload.previewMeta", "{{cols}} colonne · {{rows}} righe", {
                    cols: preview.data.columns.length,
                    rows: preview.data.total,
                  })}
                </span>
              </Label>
              {!isReupload && (
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "sources.upload.typeHint",
                    "I tipi sono dedotti automaticamente. Puoi forzarne uno prima di importare.",
                  )}
                </p>
              )}
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-surface">
                    <tr>
                      {preview.data.columns.map((c) => (
                        <th key={c.name} className="px-2 py-1.5 text-left font-medium align-top">
                          <div className="whitespace-nowrap">{c.name}</div>
                          {isReupload ? (
                            <span className="text-[10px] font-normal text-muted-foreground">
                              {c.type}
                            </span>
                          ) : (
                            <div className="mt-1 w-28">
                              <Combobox
                                options={typeOptions}
                                value={overrides[c.name] ?? c.type}
                                onChange={(next) =>
                                  setOverrides((prev) => {
                                    const out = { ...prev };
                                    if (!next || next === c.type) delete out[c.name];
                                    else out[c.name] = next;
                                    return out;
                                  })
                                }
                                className="h-7 text-xs font-normal"
                              />
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.sample.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-t border-border/60">
                        {preview.data!.columns.map((c) => (
                          <td key={c.name} className="px-2 py-1 text-muted-foreground whitespace-nowrap">
                            {row[c.name] === null || row[c.name] === undefined ? "—" : String(row[c.name])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!isReupload && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="src-up-name">{t("sources.field.name", "Nome")}</Label>
                <Input
                  id="src-up-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("sources.upload.namePlaceholder", "es. Clienti Q1")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="src-up-tags">{t("sources.field.tags", "Tag")}</Label>
                <TagsField id="src-up-tags" value={tags} onChange={setTags} />
              </div>
            </>
          )}

          {submit.error && (
            <Alert tone="danger" title={t("sources.upload.failed", "Importazione fallita")}>
              {(submit.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => submit.mutate()}>
            {submit.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <UploadCloud className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
            {t("sources.upload.submit", "Importa")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
